import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/sonner";
import api, { apiError, fmtQty, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Lock, Wallet, ArrowRightLeft, FileCheck, ListChecks, ScrollText,
         RefreshCw, ShieldAlert, Layers, Globe } from "lucide-react";

const PILL = {
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  VERIFIED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  PASS: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  COMPLETE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  APPROVED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  TAX_REVIEW_COMPLETE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  PENDING: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  RECORDED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  PARTIAL: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  REVIEW_REQUIRED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  MANUAL_REVIEW_REQUIRED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  TAX_REVIEW_PENDING: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  IN_PROGRESS: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  NOT_RUN: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  RETIRED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  FAIL: "bg-red-500/10 text-red-400 border-red-500/30",
  FAILED: "bg-red-500/10 text-red-400 border-red-500/30",
  RECONCILIATION_FAIL: "bg-red-500/10 text-red-400 border-red-500/30",
  BROKEN: "bg-red-500/10 text-red-400 border-red-500/30",
  FROZEN: "bg-red-500/10 text-red-400 border-red-500/30",
  FLAGGED: "bg-red-500/10 text-red-400 border-red-500/30",
};

function Pill({ v }) {
  return (
    <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest whitespace-nowrap ${PILL[v] || "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}>
      {(v || "—").replace(/_/g, " ")}
    </span>
  );
}

const INPUT = "bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 w-full font-mono";

function Inp({ value, onChange, testId, placeholder, type = "text" }) {
  return <input data-testid={testId} type={type} value={value} placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)} className={INPUT} />;
}

function Sel({ value, onChange, options, testId, placeholder }) {
  return (
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
      <option value="">{placeholder || "—"}</option>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

const CUSTODY_TYPES = ["SELF_CUSTODY_COLD", "SELF_CUSTODY_HOT", "EXCHANGE_CUSTODY", "INSTITUTIONAL_CUSTODY"];
const CLASSIFICATIONS = ["CORPORATE_PURCHASE", "FOUNDER_CONTRIBUTION", "INTER_WALLET_TRANSFER",
                         "CUSTODIAN_TRANSFER", "DISTRIBUTION_RECEIVED", "AIRDROP", "REWARD",
                         "OTHER_DOCUMENTED"];
const DOC_TYPES = ["EXCHANGE_ACCOUNT_STATEMENT", "EXCHANGE_WITHDRAWAL_CONFIRMATION",
                   "FOUNDER_CONTRIBUTION_MEMO", "LLC_MEMBER_RESOLUTION", "CPA_MEMO",
                   "BLOCKCHAIN_EXPLORER_EVIDENCE", "WALLET_REGISTRATION_RECORD",
                   "EXCHANGE_TRADE_HISTORY", "OTHER"];
const PROOF_METHODS = ["MANUAL_EVIDENCE_REVIEW", "TEST_TRANSACTION", "CUSTODIAN_ACCOUNT", "SIGNED_MESSAGE"];
const REVIEW_DECISIONS = ["APPROVED", "FLAGGED", "TAX_REVIEW_COMPLETE"];

const TABS = [
  ["holdings", "Custody Register", Layers], ["wallets", "Wallets", Wallet],
  ["ownership", "Ownership Events", FileCheck], ["transfers", "Transfers", ArrowRightLeft],
  ["evidence", "Evidence", ScrollText], ["onboarding", "Onboarding", ListChecks],
  ["identities", "Web3 Identities", Globe],
  ["report", "Report", ScrollText],
];

export default function Custody() {
  const { user } = useAuth();
  const isOwner = ["owner", "platform_owner", "admin"].includes(user?.role);
  const [tab, setTab] = useState("holdings");
  const [busy, setBusy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [events, setEvents] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [onboardings, setOnboardings] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [prov, setProv] = useState(null);
  const [wf, setWf] = useState({ custody_type: "SELF_CUSTODY_COLD", network: "BTC", mixed_use: false });
  const [ef, setEf] = useState({ classification: "FOUNDER_CONTRIBUTION", network: "BTC" });
  const [tf, setTf] = useState({ transfer_kind: "TEST", network: "BTC" });
  const [vf, setVf] = useState({ document_type: "EXCHANGE_WITHDRAWAL_CONFIRMATION" });
  const [stepIn, setStepIn] = useState({});
  const [reviseIn, setReviseIn] = useState({});
  const [amendIn, setAmendIn] = useState({});
  const [reconAsset, setReconAsset] = useState({});

  const load = useCallback(() => {
    api.get("/custody/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/custody/holdings").then((r) => setHoldings(r.data.holdings)).catch(() => {});
    api.get("/custody/wallets").then((r) => setWallets(r.data.wallets)).catch(() => {});
    api.get("/custody/ownership-events").then((r) => setEvents(r.data.events)).catch(() => {});
    api.get("/custody/transfers").then((r) => setTransfers(r.data.transfers)).catch(() => {});
    api.get("/custody/evidence").then((r) => setEvidence(r.data.evidence)).catch(() => {});
    api.get("/custody/onboarding").then((r) => setOnboardings(r.data.onboardings)).catch(() => {});
    api.get("/custody/identities").then((r) => setIdentities(r.data.identities)).catch(() => {});
    api.get("/custody/reports").then((r) => setReports(r.data.reports)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = (key, fn, ok) => {
    setBusy(key);
    fn().then(() => { if (ok) toast.success(ok); load(); })
        .catch((e) => toast.error(apiError(e)))
        .finally(() => setBusy(null));
  };

  const walletOptions = wallets.map((w) => ({ value: w.wallet_id, label: `${w.name} (${w.status})` }));
  const activeWallets = wallets.filter((w) => w.status === "ACTIVE");
  const exchangeWallets = activeWallets.filter((w) => w.custody_type === "EXCHANGE_CUSTODY");

  const registerWallet = () => act("wallet", () => api.post("/custody/wallets", {
    name: wf.name, legal_owner: wf.legal_owner, custody_type: wf.custody_type,
    wallet_type: wf.wallet_type || undefined, provider: wf.provider || undefined,
    network: wf.network,
    addresses: (wf.addresses || "").split("\n").map((a) => a.trim()).filter(Boolean),
    xpub: wf.xpub || undefined, authorized_custodian: wf.authorized_custodian || undefined,
    date_established: wf.date_established || undefined, mixed_use: !!wf.mixed_use,
    notes: wf.notes || undefined, policy_version: wf.policy_version || undefined,
  }), "Wallet registered — audit event written");

  const recordEvent = () => act("event", () => api.post("/custody/ownership-events", {
    asset: ef.asset, network: ef.network, quantity: Number(ef.quantity),
    legal_owner_before: ef.legal_owner_before || undefined, legal_owner_after: ef.legal_owner_after,
    classification: ef.classification,
    effective_at: ef.effective_at ? new Date(ef.effective_at).toISOString() : undefined,
    historical_cost_basis: ef.historical_cost_basis ? Number(ef.historical_cost_basis) : undefined,
    fmv_at_effective: ef.fmv_at_effective ? Number(ef.fmv_at_effective) : undefined,
    source: ef.source || undefined, destination_wallet_id: ef.destination_wallet_id || undefined,
    evidence_ids: (ef.evidence_ids || "").split(",").map((s) => s.trim()).filter(Boolean),
    notes: ef.notes || undefined,
  }), "Ownership event recorded — immutable");

  const createTransfer = () => act("transfer", () => api.post("/custody/transfers", {
    source_wallet_id: tf.source_wallet_id || undefined,
    source_label: tf.source_wallet_id ? undefined : (tf.source_label || undefined),
    destination_wallet_id: tf.destination_wallet_id,
    destination_address: tf.destination_address,
    asset: tf.asset, network: tf.network, quantity_sent: Number(tf.quantity_sent),
    tx_hash: tf.tx_hash || undefined, transfer_kind: tf.transfer_kind,
    ownership_event_id: tf.ownership_event_id || undefined,
    evidence_ids: (tf.evidence_ids || "").split(",").map((s) => s.trim()).filter(Boolean),
    fmv_at_transfer: tf.fmv_at_transfer ? Number(tf.fmv_at_transfer) : undefined,
    notes: tf.notes || undefined,
  }), "Custody transfer recorded");

  const registerEvidence = () => act("evd", () => api.post("/custody/evidence", {
    document_type: vf.document_type, file_hash: (vf.file_hash || "").trim(),
    description: vf.description || undefined,
    related: Object.fromEntries(Object.entries({
      wallet_id: vf.wallet_id, transfer_id: vf.transfer_id,
      ownership_event_id: vf.ownership_event_id, onboarding_id: vf.onboarding_id,
    }).filter(([, v]) => v)),
  }), "Evidence registered — hash committed");

  const destWallet = wallets.find((w) => w.wallet_id === tf.destination_wallet_id);

  const STEP_INPUTS = {
    wallet_registered: (onb, si, setSi) => (
      <Sel testId="step-wallet-id" value={si.wallet_id || ""}
           onChange={(v) => setSi({ ...si, wallet_id: v })} options={walletOptions}
           placeholder="Select corporate wallet" />
    ),
    source_assets_selected: (onb, si, setSi) => (
      <div className="space-y-2">
        <Sel testId="step-source-wallet" value={si.source_wallet_id || ""}
             onChange={(v) => setSi({ ...si, source_wallet_id: v })}
             options={exchangeWallets.map((w) => ({ value: w.wallet_id, label: w.name }))}
             placeholder="Source exchange wallet (e.g. Crypto.com)" />
        <div className="grid grid-cols-2 gap-2">
          <Inp testId="step-source-asset" value={si.asset || ""} onChange={(v) => setSi({ ...si, asset: v })} placeholder="Asset (BTC)" />
          <Inp testId="step-source-qty" value={si.planned_quantity || ""} onChange={(v) => setSi({ ...si, planned_quantity: v })} placeholder="Planned quantity" />
        </div>
      </div>
    ),
    ownership_classification_set: (onb, si, setSi) => (
      <Sel testId="step-classification" value={si.classification || ""}
           onChange={(v) => setSi({ ...si, classification: v })} options={CLASSIFICATIONS}
           placeholder="Ownership classification" />
    ),
    cost_basis_recorded: (onb, si, setSi) => (
      <Sel testId="step-ownership-event" value={si.ownership_event_id || ""}
           onChange={(v) => setSi({ ...si, ownership_event_id: v })}
           options={events.map((e) => ({ value: e.ownership_event_id, label: `${e.ownership_event_id} · ${e.asset} ${e.quantity} (${e.classification})` }))}
           placeholder="Ownership event with cost basis" />
    ),
    test_transfer_recorded: (onb, si, setSi) => (
      <Sel testId="step-test-transfer" value={si.transfer_id || ""}
           onChange={(v) => setSi({ ...si, transfer_id: v })}
           options={transfers.filter((t) => t.transfer_kind === "TEST").map((t) => ({ value: t.transfer_id, label: `${t.transfer_id} · ${t.asset} ${t.quantity_sent}` }))}
           placeholder="TEST transfer" />
    ),
    main_transfer_recorded: (onb, si, setSi) => (
      <Sel testId="step-main-transfer" value={si.transfer_id || ""}
           onChange={(v) => setSi({ ...si, transfer_id: v })}
           options={transfers.filter((t) => t.transfer_kind === "MAIN").map((t) => ({ value: t.transfer_id, label: `${t.transfer_id} · ${t.asset} ${t.quantity_sent}` }))}
           placeholder="MAIN transfer" />
    ),
  };

  const STEP_HINTS = {
    address_verified: "Verify proof-of-control on the wallet (Wallets tab → VERIFY CONTROL) with registered evidence, then complete this step.",
    evidence_attached: "Register the required evidence documents in the Evidence tab (LLC/member resolution + contribution memo for founder contributions; withdrawal confirmation otherwise), then complete this step.",
    test_transfer_verified: "Run VERIFY on the test transfer (Transfers tab) — blockchain receipt must pass.",
    main_transfer_verified: "Run VERIFY on the main transfer (Transfers tab) — blockchain receipt must pass.",
    exchange_balance_reconciled: "Run reconciliation on the source exchange wallet (Wallets tab → RECONCILE) — must PASS.",
    wallet_balance_reconciled: "Run reconciliation on the custody wallet (Wallets tab → RECONCILE) — on-chain balance must match the ledger.",
    report_generated: "Generate the Custody & Provenance Report (Report tab), then complete this step.",
  };

  const advanceStep = (onb, step) => {
    const si = stepIn[`${onb.onboarding_id}:${step}`] || {};
    const payload = { step };
    if (si.wallet_id) payload.wallet_id = si.wallet_id;
    if (si.source_wallet_id) payload.source_wallet_id = si.source_wallet_id;
    if (si.asset) payload.source_assets = [{ asset: si.asset, planned_quantity: Number(si.planned_quantity) }];
    if (si.classification) payload.classification = si.classification;
    if (si.ownership_event_id) payload.ownership_event_id = si.ownership_event_id;
    if (si.transfer_id) payload.transfer_id = si.transfer_id;
    act(`onb-${step}`, () => api.post(`/custody/onboarding/${onb.onboarding_id}/advance`, payload),
        `Step complete: ${step.replace(/_/g, " ")}`);
  };

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 min-w-0" data-testid="custody-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-wide text-slate-100" data-testid="custody-title">
              DIGITAL ASSET CUSTODY
            </h1>
          </div>
          <p className="text-xs font-mono text-slate-500 mt-1" data-testid="custody-doctrine">
            Custody &amp; Provenance Intelligence — Hussar verifies custody. Hussar does not take custody.
          </p>
          <p className="text-[10px] font-mono text-slate-600 mt-0.5">
            Read-only and provenance-focused. No keys, no signing, no withdrawals, no automatic transfers.
          </p>
        </div>
        {isOwner && (
          <div className="flex gap-2">
            <Button data-testid="custody-snapshot-btn" disabled={busy === "snap"} variant="outline"
                    onClick={() => act("snap", () => api.post("/custody/snapshots", {}), "Proof-of-custody snapshot recorded")}
                    className="border-[#2D323E] font-mono text-xs">
              <ShieldAlert className="w-3 h-3 mr-1" />PROOF-OF-CUSTODY
            </Button>
            <Button data-testid="custody-refresh-btn" variant="outline" onClick={load}
                    className="border-[#2D323E] font-mono text-xs">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="custody-summary">
        {[
          ["Corporate wallets", summary?.wallets, "summary-wallets"],
          ["Assets held", summary?.assets_held, "summary-assets"],
          ["Provenance complete", summary?.provenance_complete, "summary-provenance"],
          ["Reconciliation", summary?.reconciliation, "summary-recon"],
          ["Last proof-of-custody", summary?.last_proof_of_custody ? fmtTime(summary.last_proof_of_custody) : "—", "summary-poc"],
        ].map(([label, v, tid]) => (
          <div key={tid} className="panel p-3" data-testid={tid}>
            <div className="metric-label">{label}</div>
            <div className="metric-value text-sm">{v ?? "—"}</div>
          </div>
        ))}
      </div>
      {summary && !summary.clean && summary.assets_held > 0 && (
        <div className="panel p-3 border-l-2 border-l-red-500 text-[11px] font-mono text-red-300" data-testid="custody-not-clean-banner">
          Custody status is NOT CLEAN — broken provenance, unresolved variance, unverified ownership, or missing evidence exists. The register below shows exactly where.
        </div>
      )}

      <div className="flex gap-1 border-b border-[#222630] flex-wrap" data-testid="custody-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`custody-tab-${id}`} onClick={() => setTab(id)}
                  className={`px-3 py-2 text-xs font-mono tracking-wider transition-colors flex items-center gap-1.5 ${
                    tab === id ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-slate-500 hover:text-slate-300"}`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {tab === "holdings" && (
        <Panel title="Corporate Crypto Custody Register" testId="holdings-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
                <th className="py-2 px-2">Asset</th><th className="py-2 px-2">Network</th>
                <th className="py-2 px-2">Wallet / Custodian</th><th className="py-2 px-2">Legal Owner</th>
                <th className="py-2 px-2">Custody Type</th><th className="py-2 px-2 text-right">Quantity</th>
                <th className="py-2 px-2 text-right">Cost Basis</th><th className="py-2 px-2">Provenance</th>
                <th className="py-2 px-2">Reconciliation</th><th className="py-2 px-2">Last Verified</th>
                <th className="py-2 px-2"></th></tr></thead>
              <tbody>
                {holdings.map((h) => (
                  <>
                    <tr key={`${h.wallet_id}-${h.asset}`} className="border-b border-[#1E2128] hover:bg-[#222630]/40"
                        data-testid={`holding-row-${h.asset}-${h.wallet_id}`}>
                      <td className="py-2 px-2 font-mono font-bold text-slate-100">{h.asset}</td>
                      <td className="py-2 px-2 font-mono text-slate-400">{h.network}</td>
                      <td className="py-2 px-2 text-slate-300">{h.wallet_name}{h.mixed_use && <span className="ml-1 text-amber-400" title="MIXED_USE_CUSTODY_RISK">⚠</span>}</td>
                      <td className="py-2 px-2 text-slate-400">{h.legal_owner}</td>
                      <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{h.custody_type.replace(/_/g, " ")}</td>
                      <td className="py-2 px-2 font-mono text-slate-200 text-right">{fmtQty(h.quantity)}</td>
                      <td className="py-2 px-2 font-mono text-slate-400 text-right">{h.cost_basis == null ? "—" : `$${h.cost_basis.toLocaleString()}`}</td>
                      <td className="py-2 px-2" data-testid={`holding-prov-${h.asset}`}><Pill v={h.provenance_status} /></td>
                      <td className="py-2 px-2" data-testid={`holding-recon-${h.asset}`}><Pill v={h.reconciliation_status} /></td>
                      <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{fmtTime(h.last_verified_at)}</td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" variant="outline" data-testid={`provenance-btn-${h.asset}`}
                                onClick={() => {
                                  if (prov?.asset === h.asset) { setProv(null); return; }
                                  api.get(`/custody/provenance/${h.asset}`).then((r) => setProv(r.data)).catch((e) => toast.error(apiError(e)));
                                }}
                                className="h-6 text-[10px] font-mono border-[#2D323E]">TIMELINE</Button>
                      </td>
                    </tr>
                    {prov?.asset === h.asset && (
                      <tr key={`${h.wallet_id}-${h.asset}-prov`} className="border-b border-[#1E2128]">
                        <td colSpan={11} className="py-3 px-3 bg-[#0B0C0E]">
                          <div data-testid={`provenance-chain-${h.asset}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] font-mono tracking-widest text-slate-500">PROVENANCE CHAIN — {h.asset}</span>
                              <Pill v={prov.status} />
                            </div>
                            {prov.issues.length > 0 && (
                              <div className="mb-2 space-y-1">
                                {prov.issues.map((i, n) => (
                                  <div key={n} className="text-[10px] font-mono text-amber-300" data-testid={`prov-issue-${n}`}>• {i}</div>
                                ))}
                              </div>
                            )}
                            <div className="space-y-1 border-l border-[#2D323E] pl-3">
                              {prov.links.map((l, n) => (
                                <div key={n} className="text-[10px] font-mono text-slate-400" data-testid={`prov-link-${n}`}>
                                  <span className="text-slate-600">{fmtTime(l.at)}</span>
                                  <span className="text-[#D4AF37] mx-1">{l.kind}</span>
                                  {l.ref} · {l.quantity != null && `${fmtQty(l.quantity)} `}
                                  {l.from && `${l.from} → ${l.to} · `}
                                  {l.owner_after && `owner: ${l.owner_after} · `}
                                  {l.tx_hash && <span className="text-slate-500">tx {String(l.tx_hash).slice(0, 16)}… </span>}
                                  {l.status && <Pill v={l.status} />}
                                </div>
                              ))}
                              {prov.links.length === 0 && <div className="text-[10px] font-mono text-slate-600">No provenance links recorded.</div>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {holdings.length === 0 && (
                  <tr><td colSpan={11} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="holdings-empty">
                    No custody holdings recorded for this account. Register a wallet and record an ownership event to begin.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {tab === "wallets" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {isOwner && (
            <Panel title="Register Corporate Wallet" testId="wallet-register-panel">
              <div className="space-y-2">
                <Inp testId="wallet-name" value={wf.name || ""} onChange={(v) => setWf({ ...wf, name: v })} placeholder="Wallet name (e.g. Hussar Holdings Tangem Cold Custody)" />
                <Inp testId="wallet-legal-owner" value={wf.legal_owner || ""} onChange={(v) => setWf({ ...wf, legal_owner: v })} placeholder="Legal owner (e.g. Hussar Holdings LLC)" />
                <div className="grid grid-cols-2 gap-2">
                  <Sel testId="wallet-custody-type" value={wf.custody_type} onChange={(v) => setWf({ ...wf, custody_type: v })} options={CUSTODY_TYPES} />
                  <Inp testId="wallet-network" value={wf.network} onChange={(v) => setWf({ ...wf, network: v.toUpperCase() })} placeholder="Network (BTC/XRP/XLM)" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Inp testId="wallet-type" value={wf.wallet_type || ""} onChange={(v) => setWf({ ...wf, wallet_type: v })} placeholder="Wallet type (hardware card)" />
                  <Inp testId="wallet-provider" value={wf.provider || ""} onChange={(v) => setWf({ ...wf, provider: v })} placeholder="Provider (Tangem / crypto_com_exchange)" />
                </div>
                <textarea data-testid="wallet-addresses" value={wf.addresses || ""} onChange={(e) => setWf({ ...wf, addresses: e.target.value })}
                          placeholder="Public receiving addresses — one per line" rows={3} className={INPUT} />
                <Inp testId="wallet-xpub" value={wf.xpub || ""} onChange={(v) => setWf({ ...wf, xpub: v })} placeholder="xPub (optional, read-only)" />
                <div className="grid grid-cols-2 gap-2">
                  <Inp testId="wallet-custodian" value={wf.authorized_custodian || ""} onChange={(v) => setWf({ ...wf, authorized_custodian: v })} placeholder="Authorized custodian" />
                  <Inp testId="wallet-established" type="date" value={wf.date_established || ""} onChange={(v) => setWf({ ...wf, date_established: v })} placeholder="Date established" />
                </div>
                <label className="flex items-center gap-2 text-[10px] font-mono text-slate-400">
                  <input type="checkbox" data-testid="wallet-mixed-use" checked={!!wf.mixed_use}
                         onChange={(e) => setWf({ ...wf, mixed_use: e.target.checked })} />
                  Mixed-use wallet (triggers MIXED_USE_CUSTODY_RISK)
                </label>
                <Inp testId="wallet-notes" value={wf.notes || ""} onChange={(v) => setWf({ ...wf, notes: v })} placeholder="Notes" />
                <div className="text-[9px] font-mono text-slate-600 border border-[#222630] rounded-sm p-2">
                  Never enter seed phrases, private keys, access codes, recovery secrets, or signing credentials. Public/read-only data only — secret-shaped fields are rejected.
                </div>
                <Button data-testid="wallet-register-btn" disabled={busy === "wallet"} onClick={registerWallet}
                        className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                  {busy === "wallet" ? "REGISTERING…" : "REGISTER WALLET"}
                </Button>
              </div>
            </Panel>
          )}
          <div className="xl:col-span-2 space-y-3" data-testid="wallets-list">
            {wallets.map((w) => (
              <Panel key={w.wallet_id} title={w.name} testId={`wallet-card-${w.wallet_id}`}>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill v={w.status} />
                    <span className="text-[10px] font-mono text-slate-500">{w.custody_type.replace(/_/g, " ")} · {w.network}</span>
                    <span className="text-[10px] font-mono text-slate-600">{w.wallet_id}</span>
                    {w.mixed_use && !w.mixed_use_approved_by && (
                      <span className="px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest bg-amber-500/10 text-amber-300 border-amber-500/30"
                            data-testid={`mixed-use-badge-${w.wallet_id}`}>MIXED-USE CUSTODY RISK</span>
                    )}
                    {w.mixed_use && w.mixed_use_approved_by && (
                      <span className="px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest bg-slate-500/10 text-slate-400 border-slate-500/30"
                            data-testid={`mixed-use-approved-${w.wallet_id}`}>MIXED-USE APPROVED BY {w.mixed_use_approved_by}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-slate-400">
                    <span>Legal owner <span className="text-slate-200 float-right">{w.legal_owner}</span></span>
                    <span>Provider <span className="text-slate-200 float-right">{w.provider || "—"}</span></span>
                    <span>Authorized custodian <span className="text-slate-200 float-right">{w.authorized_custodian || "—"}</span></span>
                    <span>Established <span className="text-slate-200 float-right">{w.date_established || "—"}</span></span>
                    <span>Proof of control <span className="float-right"><Pill v={w.proof_of_control?.status} /></span></span>
                    <span>Policy version <span className="text-slate-200 float-right">{w.policy_version || "—"}</span></span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 space-y-0.5">
                    {w.addresses.map((a) => <div key={a} data-testid={`wallet-addr-${w.wallet_id}`}>addr: {a}</div>)}
                    {w.xpub && <div>xpub: {w.xpub.slice(0, 24)}…</div>}
                  </div>
                  {w.pending_change && (
                    <div className="border border-amber-500/30 bg-amber-950/20 rounded-sm p-2 text-[10px] font-mono" data-testid={`pending-change-${w.wallet_id}`}>
                      <div className="text-amber-300 font-bold tracking-widest mb-1">PENDING CHANGE — DUAL REVIEW REQUIRED</div>
                      <div className="text-slate-400">proposed by {w.pending_change.proposed_by}: {JSON.stringify(w.pending_change.fields)}</div>
                      {isOwner && (
                        <Button size="sm" data-testid={`approve-change-${w.wallet_id}`} disabled={busy === `ap-${w.wallet_id}`}
                                onClick={() => act(`ap-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/approve-change`, {}), "Change approved and applied")}
                                className="mt-2 bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 h-6 text-[10px] font-mono">
                          APPROVE CHANGE (second reviewer)
                        </Button>
                      )}
                    </div>
                  )}
                  {isOwner && (
                    <div className="flex gap-2 flex-wrap pt-1 border-t border-[#1E2128]">
                      {!w.proof_of_control || w.proof_of_control.status !== "VERIFIED" ? (
                        <Button size="sm" variant="outline" data-testid={`verify-control-${w.wallet_id}`} disabled={busy === `vc-${w.wallet_id}`}
                                onClick={() => {
                                  const eid = (stepIn[`evd-${w.wallet_id}`] || "").trim();
                                  act(`vc-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/verify-control`, {
                                    method: "MANUAL_EVIDENCE_REVIEW", evidence_ids: eid ? [eid] : [],
                                  }), "Proof of control VERIFIED");
                                }}
                                className="h-6 text-[10px] font-mono border-[#2D323E]">
                          VERIFY CONTROL
                        </Button>
                      ) : null}
                      {(!w.proof_of_control || w.proof_of_control.status !== "VERIFIED") && (
                        <Inp testId={`verify-evidence-${w.wallet_id}`} value={stepIn[`evd-${w.wallet_id}`] || ""}
                             onChange={(v) => setStepIn({ ...stepIn, [`evd-${w.wallet_id}`]: v })} placeholder="evidence id (EVD-…)" />
                      )}
                      <Inp testId={`recon-asset-${w.wallet_id}`} value={reconAsset[w.wallet_id] || ""}
                           onChange={(v) => setReconAsset({ ...reconAsset, [w.wallet_id]: v.toUpperCase() })} placeholder="Asset (BTC)" />
                      <Button size="sm" variant="outline" data-testid={`reconcile-${w.wallet_id}`} disabled={busy === `rec-${w.wallet_id}`}
                              onClick={() => act(`rec-${w.wallet_id}`, () => api.post("/custody/reconcile", {
                                wallet_id: w.wallet_id, asset: reconAsset[w.wallet_id],
                              }).then((r) => {
                                if (r.data.status === "PASS") toast.success(`Reconciliation PASS (expected ${r.data.expected_quantity}, observed ${r.data.observed_quantity})`);
                                else toast.error(`Reconciliation ${r.data.status} — variance ${r.data.variance}`);
                                return r;
                              }), null)}
                              className="h-6 text-[10px] font-mono border-[#2D323E]">
                        RECONCILE
                      </Button>
                      {w.status === "ACTIVE" && (
                        <Button size="sm" variant="outline" data-testid={`freeze-${w.wallet_id}`}
                                onClick={() => act(`st-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/status`, { status: "FROZEN", note: "manual freeze" }), "Wallet FROZEN")}
                                className="h-6 text-[10px] font-mono border-red-500/40 text-red-400">FREEZE</Button>
                      )}
                      {w.status === "FROZEN" && (
                        <Button size="sm" variant="outline" data-testid={`unfreeze-${w.wallet_id}`}
                                onClick={() => act(`st-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/status`, { status: "ACTIVE", note: "manual unfreeze" }), "Wallet reactivated")}
                                className="h-6 text-[10px] font-mono border-[#2D323E]">UNFREEZE</Button>
                      )}
                      {w.status !== "RETIRED" && (
                        <Button size="sm" variant="outline" data-testid={`retire-${w.wallet_id}`}
                                onClick={() => act(`st-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/status`, { status: "RETIRED", note: "manual retirement" }), "Wallet RETIRED")}
                                className="h-6 text-[10px] font-mono border-slate-500/40 text-slate-400">RETIRE</Button>
                      )}
                      {w.mixed_use && !w.mixed_use_approved_by && (
                        <>
                          <Inp testId={`mixed-note-${w.wallet_id}`} value={stepIn[`mu-${w.wallet_id}`] || ""}
                               onChange={(v) => setStepIn({ ...stepIn, [`mu-${w.wallet_id}`]: v })} placeholder="approval note" />
                          <Button size="sm" variant="outline" data-testid={`mixed-approve-${w.wallet_id}`}
                                  onClick={() => act(`mu-${w.wallet_id}`, () => api.post(`/custody/wallets/${w.wallet_id}/mixed-use-approve`, { note: stepIn[`mu-${w.wallet_id}`] || "" }), "Mixed-use manually approved")}
                                  className="h-6 text-[10px] font-mono border-amber-500/40 text-amber-300">APPROVE MIXED-USE</Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Panel>
            ))}
            {wallets.length === 0 && (
              <div className="panel p-8 text-center text-slate-600 font-mono text-xs" data-testid="wallets-empty">
                No wallets registered for this account.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "ownership" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {isOwner && (
            <Panel title="Record Ownership Event (immutable)" testId="event-record-panel">
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Inp testId="event-asset" value={ef.asset || ""} onChange={(v) => setEf({ ...ef, asset: v.toUpperCase() })} placeholder="Asset" />
                  <Inp testId="event-network" value={ef.network} onChange={(v) => setEf({ ...ef, network: v.toUpperCase() })} placeholder="Network" />
                  <Inp testId="event-quantity" value={ef.quantity || ""} onChange={(v) => setEf({ ...ef, quantity: v })} placeholder="Quantity" />
                </div>
                <Sel testId="event-classification" value={ef.classification} onChange={(v) => setEf({ ...ef, classification: v })} options={CLASSIFICATIONS} />
                <div className="grid grid-cols-2 gap-2">
                  <Inp testId="event-owner-before" value={ef.legal_owner_before || ""} onChange={(v) => setEf({ ...ef, legal_owner_before: v })} placeholder="Legal owner before" />
                  <Inp testId="event-owner-after" value={ef.legal_owner_after || ""} onChange={(v) => setEf({ ...ef, legal_owner_after: v })} placeholder="Legal owner after" />
                </div>
                <Inp testId="event-effective" type="datetime-local" value={ef.effective_at || ""} onChange={(v) => setEf({ ...ef, effective_at: v })} />
                <div className="grid grid-cols-2 gap-2">
                  <Inp testId="event-cost-basis" value={ef.historical_cost_basis || ""} onChange={(v) => setEf({ ...ef, historical_cost_basis: v })} placeholder="Historical cost basis (USD)" />
                  <Inp testId="event-fmv" value={ef.fmv_at_effective || ""} onChange={(v) => setEf({ ...ef, fmv_at_effective: v })} placeholder="FMV at effective time (USD)" />
                </div>
                <Inp testId="event-source" value={ef.source || ""} onChange={(v) => setEf({ ...ef, source: v })} placeholder="Source account / custodian" />
                <Sel testId="event-dest-wallet" value={ef.destination_wallet_id || ""} onChange={(v) => setEf({ ...ef, destination_wallet_id: v })} options={walletOptions} placeholder="Destination wallet" />
                <Inp testId="event-evidence" value={ef.evidence_ids || ""} onChange={(v) => setEf({ ...ef, evidence_ids: v })} placeholder="Evidence ids (comma separated)" />
                <Inp testId="event-notes" value={ef.notes || ""} onChange={(v) => setEf({ ...ef, notes: v })} placeholder="Notes" />
                <div className="text-[9px] font-mono text-slate-600 border border-[#222630] rounded-sm p-2">
                  Posted events are immutable. Corrections happen through explicit amendment events — never silent edits. Tax treatment defaults to TAX_REVIEW_PENDING and stays subject to accounting/legal review.
                </div>
                <Button data-testid="event-record-btn" disabled={busy === "event"} onClick={recordEvent}
                        className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                  {busy === "event" ? "RECORDING…" : "RECORD EVENT"}
                </Button>
              </div>
            </Panel>
          )}
          <div className="xl:col-span-2">
            <Panel title="Ownership Events" testId="events-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
                    <th className="py-2 px-2">Event</th><th className="py-2 px-2">Asset</th>
                    <th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2">Classification</th>
                    <th className="py-2 px-2">Owner before → after</th><th className="py-2 px-2">Effective</th>
                    <th className="py-2 px-2 text-right">Basis</th><th className="py-2 px-2">Tax</th>
                    <th className="py-2 px-2">Review</th><th className="py-2 px-2"></th></tr></thead>
                  <tbody>
                    {events.map((e) => (
                      <>
                        <tr key={e.ownership_event_id} className="border-b border-[#1E2128]" data-testid={`event-row-${e.ownership_event_id}`}>
                          <td className="py-2 px-2 font-mono text-[#D4AF37]">{e.ownership_event_id}
                            {e.event_kind === "AMENDMENT" && <span className="text-slate-500"> (amends {e.amends})</span>}
                            {e.superseded && <span className="ml-1 text-[9px] text-slate-500">[SUPERSEDED]</span>}
                          </td>
                          <td className="py-2 px-2 font-mono text-slate-200">{e.asset}</td>
                          <td className="py-2 px-2 font-mono text-slate-200 text-right">{fmtQty(e.quantity)}</td>
                          <td className="py-2 px-2 font-mono text-slate-400 text-[10px]">{e.classification.replace(/_/g, " ")}</td>
                          <td className="py-2 px-2 font-mono text-slate-400 text-[10px]">{e.legal_owner_before || "—"} → {e.legal_owner_after}</td>
                          <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{fmtTime(e.effective_at)}</td>
                          <td className="py-2 px-2 font-mono text-slate-400 text-right">{e.historical_cost_basis == null ? "—" : `$${e.historical_cost_basis.toLocaleString()}`}</td>
                          <td className="py-2 px-2"><Pill v={e.tax_treatment_status} /></td>
                          <td className="py-2 px-2" data-testid={`event-approval-${e.ownership_event_id}`}><Pill v={e.approval_state} /></td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            {isOwner && (
                              <>
                                <Button size="sm" variant="outline" data-testid={`amend-btn-${e.ownership_event_id}`}
                                        onClick={() => setAmendIn({ ...amendIn, [e.ownership_event_id]: amendIn[e.ownership_event_id] ? null : {} })}
                                        className="h-6 text-[10px] font-mono border-[#2D323E] mr-1">AMEND</Button>
                                <Sel testId={`review-${e.ownership_event_id}`} value=""
                                     onChange={(v) => v && act(`rev-${e.ownership_event_id}`, () => api.post(`/custody/ownership-events/${e.ownership_event_id}/review`, { decision: v }), `Review recorded: ${v}`)}
                                     options={REVIEW_DECISIONS} placeholder="REVIEW…" />
                              </>
                            )}
                          </td>
                        </tr>
                        {amendIn[e.ownership_event_id] && (
                          <tr key={`${e.ownership_event_id}-amend`} className="border-b border-[#1E2128] bg-[#0B0C0E]">
                            <td colSpan={10} className="py-2 px-3">
                              <div className="grid grid-cols-4 gap-2">
                                <Inp testId={`amend-reason-${e.ownership_event_id}`} value={amendIn[e.ownership_event_id].reason || ""}
                                     onChange={(v) => setAmendIn({ ...amendIn, [e.ownership_event_id]: { ...amendIn[e.ownership_event_id], reason: v } })}
                                     placeholder="Amendment reason (required)" />
                                <Inp testId={`amend-qty-${e.ownership_event_id}`} value={amendIn[e.ownership_event_id].quantity || ""}
                                     onChange={(v) => setAmendIn({ ...amendIn, [e.ownership_event_id]: { ...amendIn[e.ownership_event_id], quantity: v } })}
                                     placeholder="New quantity (optional)" />
                                <Inp testId={`amend-basis-${e.ownership_event_id}`} value={amendIn[e.ownership_event_id].basis || ""}
                                     onChange={(v) => setAmendIn({ ...amendIn, [e.ownership_event_id]: { ...amendIn[e.ownership_event_id], basis: v } })}
                                     placeholder="New cost basis (optional)" />
                                <Button size="sm" data-testid={`amend-submit-${e.ownership_event_id}`}
                                        disabled={busy === `am-${e.ownership_event_id}`}
                                        onClick={() => {
                                          const a = amendIn[e.ownership_event_id];
                                          act(`am-${e.ownership_event_id}`, () => api.post(`/custody/ownership-events/${e.ownership_event_id}/amend`, {
                                            amendment_reason: a.reason,
                                            ...(a.quantity ? { quantity: Number(a.quantity) } : {}),
                                            ...(a.basis ? { historical_cost_basis: Number(a.basis) } : {}),
                                          }), "Amendment recorded — original preserved");
                                        }}
                                        className="bg-amber-500/20 text-amber-300 border border-amber-500/40 font-mono text-[10px]">
                                  SUBMIT AMENDMENT
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                    {events.length === 0 && (
                      <tr><td colSpan={10} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="events-empty">No ownership events recorded.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {tab === "transfers" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {isOwner && (
            <Panel title="Record Custody Transfer" testId="transfer-create-panel">
              <div className="space-y-2">
                <Sel testId="transfer-source-wallet" value={tf.source_wallet_id || ""}
                     onChange={(v) => setTf({ ...tf, source_wallet_id: v })}
                     options={walletOptions} placeholder="Source wallet (or external below)" />
                {!tf.source_wallet_id && (
                  <Inp testId="transfer-source-label" value={tf.source_label || ""} onChange={(v) => setTf({ ...tf, source_label: v })} placeholder="External source (e.g. Crypto.com Exchange)" />
                )}
                <Sel testId="transfer-dest-wallet" value={tf.destination_wallet_id || ""}
                     onChange={(v) => setTf({ ...tf, destination_wallet_id: v, destination_address: "" })}
                     options={activeWallets.map((w) => ({ value: w.wallet_id, label: w.name }))} placeholder="Destination wallet" />
                {destWallet && (
                  <Sel testId="transfer-dest-address" value={tf.destination_address || ""}
                       onChange={(v) => setTf({ ...tf, destination_address: v })}
                       options={destWallet.addresses} placeholder="Destination address (registered)" />
                )}
                <div className="grid grid-cols-3 gap-2">
                  <Inp testId="transfer-asset" value={tf.asset || ""} onChange={(v) => setTf({ ...tf, asset: v.toUpperCase() })} placeholder="Asset" />
                  <Inp testId="transfer-network" value={tf.network} onChange={(v) => setTf({ ...tf, network: v.toUpperCase() })} placeholder="Network" />
                  <Inp testId="transfer-quantity" value={tf.quantity_sent || ""} onChange={(v) => setTf({ ...tf, quantity_sent: v })} placeholder="Quantity" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Sel testId="transfer-kind" value={tf.transfer_kind} onChange={(v) => setTf({ ...tf, transfer_kind: v })} options={["TEST", "MAIN"]} />
                  <Inp testId="transfer-txhash" value={tf.tx_hash || ""} onChange={(v) => setTf({ ...tf, tx_hash: v.trim() })} placeholder="Transaction hash" />
                </div>
                <Sel testId="transfer-event" value={tf.ownership_event_id || ""} onChange={(v) => setTf({ ...tf, ownership_event_id: v })}
                     options={events.map((e) => ({ value: e.ownership_event_id, label: `${e.ownership_event_id} · ${e.asset}` }))} placeholder="Ownership classification ref (optional)" />
                <Inp testId="transfer-evidence" value={tf.evidence_ids || ""} onChange={(v) => setTf({ ...tf, evidence_ids: v })} placeholder="Evidence ids (comma separated)" />
                <Inp testId="transfer-notes" value={tf.notes || ""} onChange={(v) => setTf({ ...tf, notes: v })} placeholder="Notes" />
                <Button data-testid="transfer-create-btn" disabled={busy === "transfer"} onClick={createTransfer}
                        className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                  {busy === "transfer" ? "RECORDING…" : "RECORD TRANSFER"}
                </Button>
              </div>
            </Panel>
          )}
          <div className="xl:col-span-2">
            <Panel title="Custody Transfers" testId="transfers-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
                    <th className="py-2 px-2">Transfer</th><th className="py-2 px-2">Route</th>
                    <th className="py-2 px-2">Asset</th><th className="py-2 px-2 text-right">Qty sent</th>
                    <th className="py-2 px-2 text-right">Qty received</th><th className="py-2 px-2">Tx hash</th>
                    <th className="py-2 px-2">Confirmations</th><th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Recon</th><th className="py-2 px-2"></th></tr></thead>
                  <tbody>
                    {transfers.map((t) => (
                      <>
                        <tr key={t.transfer_id} className="border-b border-[#1E2128]" data-testid={`transfer-row-${t.transfer_id}`}>
                          <td className="py-2 px-2 font-mono text-[#D4AF37]">{t.transfer_id}
                            <span className="ml-1 text-[9px] text-slate-500">{t.transfer_kind}</span></td>
                          <td className="py-2 px-2 font-mono text-slate-400 text-[10px]">{t.source_label} → {t.destination_label}</td>
                          <td className="py-2 px-2 font-mono text-slate-200">{t.asset}/{t.network}</td>
                          <td className="py-2 px-2 font-mono text-slate-200 text-right">{fmtQty(t.quantity_sent)}</td>
                          <td className="py-2 px-2 font-mono text-slate-200 text-right">{t.quantity_received == null ? "—" : fmtQty(t.quantity_received)}</td>
                          <td className="py-2 px-2 font-mono text-slate-500 text-[10px]" title={t.tx_hash}>{t.tx_hash ? `${t.tx_hash.slice(0, 12)}…` : "—"}</td>
                          <td className="py-2 px-2 font-mono text-slate-400">{t.confirmation_count ?? "—"}</td>
                          <td className="py-2 px-2" data-testid={`transfer-status-${t.transfer_id}`}><Pill v={t.transaction_status} /></td>
                          <td className="py-2 px-2" data-testid={`transfer-recon-${t.transfer_id}`}><Pill v={t.reconciliation_state} /></td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            {isOwner && t.reconciliation_state !== "VERIFIED" && t.transaction_status !== "VOIDED" && (
                              <>
                                <Button size="sm" variant="outline" data-testid={`verify-transfer-${t.transfer_id}`}
                                        disabled={busy === `vt-${t.transfer_id}`}
                                        onClick={() => act(`vt-${t.transfer_id}`, () => api.post(`/custody/transfers/${t.transfer_id}/verify`, {}).then((r) => {
                                          if (r.data.reconciliation_state === "VERIFIED") toast.success("Blockchain verification PASSED");
                                          else toast.error(`Verification unresolved: ${(r.data.verification?.reasons || ["manual review required"]).join("; ")}`);
                                          return r;
                                        }), null)}
                                        className="h-6 text-[10px] font-mono border-[#2D323E]">VERIFY</Button>
                                <Button size="sm" variant="outline" data-testid={`void-transfer-${t.transfer_id}`}
                                        disabled={busy === `vd-${t.transfer_id}`}
                                        onClick={() => {
                                          const reason = window.prompt("Void reason (min 10 chars — permanently recorded in the audit chain):");
                                          if (reason) act(`vd-${t.transfer_id}`, () => api.post(`/custody/transfers/${t.transfer_id}/void`, { reason }), "Transfer voided — disposition audit-chained");
                                        }}
                                        className="h-6 text-[10px] font-mono border-red-900/60 text-red-400 ml-1">VOID</Button>
                              </>
                            )}
                          </td>
                        </tr>
                        {t.void && (
                          <tr key={`${t.transfer_id}-void`} className="border-b border-[#1E2128] bg-[#0B0C0E]">
                            <td colSpan={10} className="py-1.5 px-3 text-[10px] font-mono text-slate-400" data-testid={`transfer-void-reason-${t.transfer_id}`}>
                              VOIDED by {t.void.by} · {fmtTime(t.void.at)} — {t.void.reason}
                            </td>
                          </tr>
                        )}
                        {t.verification && !t.verification.ok && (t.verification.reasons || []).length > 0 && (
                          <tr key={`${t.transfer_id}-reasons`} className="border-b border-[#1E2128] bg-[#0B0C0E]">
                            <td colSpan={10} className="py-1.5 px-3 text-[10px] font-mono text-red-300" data-testid={`transfer-fail-reasons-${t.transfer_id}`}>
                              {t.verification.reasons.map((r, n) => <div key={n}>• {r}</div>)}
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                    {transfers.length === 0 && (
                      <tr><td colSpan={10} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="transfers-empty">No custody transfers recorded.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {tab === "evidence" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {isOwner && (
            <Panel title="Register Evidence (hash-only)" testId="evidence-register-panel">
              <div className="space-y-2">
                <Sel testId="evidence-doc-type" value={vf.document_type} onChange={(v) => setVf({ ...vf, document_type: v })} options={DOC_TYPES} />
                <Inp testId="evidence-hash" value={vf.file_hash || ""} onChange={(v) => setVf({ ...vf, file_hash: v })} placeholder="SHA-256 of the document (64 hex chars)" />
                <Inp testId="evidence-description" value={vf.description || ""} onChange={(v) => setVf({ ...vf, description: v })} placeholder="Description" />
                <Sel testId="evidence-wallet" value={vf.wallet_id || ""} onChange={(v) => setVf({ ...vf, wallet_id: v })} options={walletOptions} placeholder="Related wallet (optional)" />
                <Sel testId="evidence-transfer" value={vf.transfer_id || ""} onChange={(v) => setVf({ ...vf, transfer_id: v })}
                     options={transfers.map((t) => ({ value: t.transfer_id, label: `${t.transfer_id} · ${t.asset}` }))} placeholder="Related transfer (optional)" />
                <Sel testId="evidence-event" value={vf.ownership_event_id || ""} onChange={(v) => setVf({ ...vf, ownership_event_id: v })}
                     options={events.map((e) => ({ value: e.ownership_event_id, label: `${e.ownership_event_id} · ${e.asset}` }))} placeholder="Related ownership event (optional)" />
                <div className="text-[9px] font-mono text-slate-600 border border-[#222630] rounded-sm p-2">
                  Hash-only custody: the document stays in your files; Treasury stores its SHA-256 commitment. Revisions create a new version — evidence is never silently replaced.
                </div>
                <Button data-testid="evidence-register-btn" disabled={busy === "evd"} onClick={registerEvidence}
                        className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                  {busy === "evd" ? "REGISTERING…" : "REGISTER EVIDENCE"}
                </Button>
              </div>
            </Panel>
          )}
          <div className="xl:col-span-2">
            <Panel title="Evidence Registry" testId="evidence-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
                    <th className="py-2 px-2">Evidence</th><th className="py-2 px-2">Document type</th>
                    <th className="py-2 px-2">SHA-256</th><th className="py-2 px-2">Version</th>
                    <th className="py-2 px-2">Related</th><th className="py-2 px-2">Uploaded</th>
                    <th className="py-2 px-2"></th></tr></thead>
                  <tbody>
                    {evidence.map((d) => (
                      <>
                        <tr key={d.evidence_id} className="border-b border-[#1E2128]" data-testid={`evidence-row-${d.evidence_id}`}>
                          <td className="py-2 px-2 font-mono text-[#D4AF37]">{d.evidence_id}
                            {d.superseded && <span className="ml-1 text-[9px] text-slate-500">[SUPERSEDED]</span>}</td>
                          <td className="py-2 px-2 font-mono text-slate-300 text-[10px]">{d.document_type.replace(/_/g, " ")}</td>
                          <td className="py-2 px-2 font-mono text-slate-500 text-[10px]" title={d.file_hash}>{d.file_hash.slice(0, 16)}…</td>
                          <td className="py-2 px-2 font-mono text-slate-400">v{d.version}</td>
                          <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{Object.values(d.related || {}).join(", ") || "—"}</td>
                          <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{fmtTime(d.uploaded_at)}</td>
                          <td className="py-2 px-2 text-right">
                            {isOwner && !d.superseded && (
                              <Button size="sm" variant="outline" data-testid={`revise-btn-${d.evidence_id}`}
                                      onClick={() => setReviseIn({ ...reviseIn, [d.evidence_id]: reviseIn[d.evidence_id] ? null : {} })}
                                      className="h-6 text-[10px] font-mono border-[#2D323E]">REVISE</Button>
                            )}
                          </td>
                        </tr>
                        {reviseIn[d.evidence_id] && (
                          <tr key={`${d.evidence_id}-revise`} className="border-b border-[#1E2128] bg-[#0B0C0E]">
                            <td colSpan={7} className="py-2 px-3">
                              <div className="grid grid-cols-3 gap-2">
                                <Inp testId={`revise-hash-${d.evidence_id}`} value={reviseIn[d.evidence_id].hash || ""}
                                     onChange={(v) => setReviseIn({ ...reviseIn, [d.evidence_id]: { ...reviseIn[d.evidence_id], hash: v } })}
                                     placeholder="New SHA-256 (64 hex)" />
                                <Inp testId={`revise-reason-${d.evidence_id}`} value={reviseIn[d.evidence_id].reason || ""}
                                     onChange={(v) => setReviseIn({ ...reviseIn, [d.evidence_id]: { ...reviseIn[d.evidence_id], reason: v } })}
                                     placeholder="Revision reason (required)" />
                                <Button size="sm" data-testid={`revise-submit-${d.evidence_id}`}
                                        disabled={busy === `rv-${d.evidence_id}`}
                                        onClick={() => act(`rv-${d.evidence_id}`, () => api.post(`/custody/evidence/${d.evidence_id}/revise`, {
                                          file_hash: reviseIn[d.evidence_id].hash,
                                          revision_reason: reviseIn[d.evidence_id].reason,
                                        }), "Evidence revised — new version created, original preserved")}
                                        className="bg-amber-500/20 text-amber-300 border border-amber-500/40 font-mono text-[10px]">
                                  SUBMIT REVISION
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                    {evidence.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="evidence-empty">No evidence registered.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {tab === "onboarding" && (
        <div className="space-y-4">
          {isOwner && (
            <div className="flex justify-end">
              <Button data-testid="onboarding-start-btn" disabled={busy === "onb-new"}
                      onClick={() => act("onb-new", () => api.post("/custody/onboarding", {}), "Custody onboarding initiated")}
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs">
                START CUSTODY ONBOARDING
              </Button>
            </div>
          )}
          {onboardings.map((onb) => {
            const nextStep = Object.entries(onb.steps).find(([, s]) => s.status !== "COMPLETE");
            return (
              <Panel key={onb.onboarding_id} title={`${onb.title} — ${onb.status}`} testId={`onboarding-${onb.onboarding_id}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    {Object.entries(onb.steps).map(([key, s], idx) => (
                      <div key={key} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`onb-step-${onb.onboarding_id}-${key}`}>
                        <span className="text-slate-600 w-5">{idx + 1}.</span>
                        <Pill v={s.status} />
                        <span className={s.status === "COMPLETE" ? "text-slate-500" : "text-slate-200"}>{s.label}</span>
                        {s.at && <span className="text-slate-600 text-[9px]">{fmtTime(s.at)}</span>}
                      </div>
                    ))}
                  </div>
                  <div>
                    {onb.status === "IN_PROGRESS" && nextStep && isOwner && (
                      <div className="border border-[#2D323E] rounded-sm p-3 space-y-2" data-testid={`onb-next-${onb.onboarding_id}`}>
                        <div className="text-[10px] font-mono tracking-widest text-[#D4AF37]">NEXT STEP: {nextStep[1].label.toUpperCase()}</div>
                        {STEP_HINTS[nextStep[0]] && (
                          <div className="text-[10px] font-mono text-slate-500">{STEP_HINTS[nextStep[0]]}</div>
                        )}
                        {STEP_INPUTS[nextStep[0]]?.(onb, stepIn[`${onb.onboarding_id}:${nextStep[0]}`] || {},
                          (si) => setStepIn({ ...stepIn, [`${onb.onboarding_id}:${nextStep[0]}`]: si }))}
                        <Button size="sm" data-testid={`onb-advance-${onb.onboarding_id}`}
                                disabled={busy === `onb-${nextStep[0]}`}
                                onClick={() => advanceStep(onb, nextStep[0])}
                                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-[10px]">
                          COMPLETE STEP
                        </Button>
                      </div>
                    )}
                    {onb.status === "IN_PROGRESS" && !nextStep && isOwner && (
                      <Button data-testid={`onb-close-${onb.onboarding_id}`} disabled={busy === "onb-close"}
                              onClick={() => act("onb-close", () => api.post(`/custody/onboarding/${onb.onboarding_id}/close`, {}), "Onboarding COMPLETE — wallet is an approved corporate custody location")}
                              className="bg-emerald-600 text-white hover:bg-emerald-500 font-mono text-xs">
                        CLOSE ONBOARDING (all gates pass)
                      </Button>
                    )}
                  </div>
                </div>
              </Panel>
            );
          })}
          {onboardings.length === 0 && (
            <div className="panel p-8 text-center text-slate-600 font-mono text-xs" data-testid="onboarding-empty">
              No custody onboarding workflows. Start one to establish a corporate wallet as an approved Treasury custody location (Crypto.com → self-custody).
            </div>
          )}
        </div>
      )}

      {tab === "identities" && (
        <Panel title="Corporate Web3 Identities" testId="identities-panel">
          <div className="space-y-3">
            {identities.map((i) => (
              <div key={i.identity_id} data-testid={`identity-${i.identity_id}`}
                   className="border border-[#222630] rounded-sm p-4 bg-[#0B0C0E]">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono text-sm text-[#D4AF37]" data-testid={`identity-name-${i.identity_id}`}>{i.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      {i.asset_type?.replace(/_/g, " ")} · {i.registrar || "registrar unrecorded"} · {i.legal_owner}
                    </div>
                  </div>
                  <Pill v={i.status} />
                </div>
                {i.use && <div className="mt-2 text-xs text-slate-300">{i.use}</div>}
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`identity-doctrine-${i.identity_id}`}>
                  {[["PUBLIC SOLICITATION", i.public_solicitation], ["CUSTOMER CUSTODY", i.customer_custody],
                    ["AUTO-PAYMENT", i.auto_payment_access], ["DISPOSITION", i.disposition],
                    ["VALUATION", i.valuation_status]].map(([k, v]) => (
                    <span key={k} className="px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest bg-slate-500/10 text-slate-300 border-slate-500/30">
                      {k}: {(v || "—").replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-[10px] font-mono text-amber-300/80" data-testid={`identity-mapping-${i.identity_id}`}>
                  {(i.mapped_addresses || []).length === 0
                    ? "NO APPROVED ADDRESS MAPPING — the domain is not a wallet and cannot receive assets."
                    : `${i.mapped_addresses.length} approved address mapping(s).`}
                </div>
                {i.status === "PENDING" && i.assignment_status && (
                  <div className="mt-1 text-[10px] font-mono text-slate-500">{i.assignment_status}</div>
                )}
                {i.notes && <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">{i.notes}</div>}
              </div>
            ))}
            {identities.length === 0 && (
              <div className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="identities-empty">
                No corporate Web3 identities registered for this account.
              </div>
            )}
          </div>
        </Panel>
      )}

      {tab === "report" && (
        <div className="space-y-4">
          <div className="flex gap-2 justify-end">
            {isOwner && (
              <Button data-testid="report-generate-btn" disabled={busy === "report"}
                      onClick={() => act("report", () => api.get("/custody/report").then((r) => { setReport(r.data); return r; }), "Report generated")}
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs">
                {busy === "report" ? "GENERATING…" : "GENERATE CUSTODY & PROVENANCE REPORT"}
              </Button>
            )}
            {report && (
              <>
                <Button variant="outline" data-testid="report-download-btn"
                        onClick={() => {
                          const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `custody-provenance-report-${report.report_id}.json`;
                          a.click();
                        }}
                        className="border-[#2D323E] font-mono text-xs">DOWNLOAD JSON</Button>
                <Button variant="outline" data-testid="report-print-btn" onClick={() => window.print()}
                        className="border-[#2D323E] font-mono text-xs">PRINT / SAVE PDF</Button>
              </>
            )}
          </div>
          {reports.length > 0 && !report && (
            <Panel title="Generated Reports" testId="reports-list-panel">
              <div className="space-y-1">
                {reports.map((r) => (
                  <div key={r.report_id} className="text-[11px] font-mono text-slate-400" data-testid={`report-row-${r.report_id}`}>
                    {r.report_id} · {fmtTime(r.generated_at)} · by {r.generated_by} · hash {r.content_hash.slice(0, 16)}…
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {report && (
            <Panel title={report.body.report} testId="report-view" className="print:bg-white">
              <div className="space-y-4 text-xs font-mono" data-testid="report-content">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-slate-400">
                  <span>Legal entity <span className="text-slate-100 block">{report.body.legal_entity}</span></span>
                  <span>Report id <span className="text-slate-100 block">{report.report_id}</span></span>
                  <span>Generated <span className="text-slate-100 block">{fmtTime(report.generated_at)}</span></span>
                  <span>Content hash <span className="text-slate-100 block">{report.content_hash.slice(0, 24)}…</span></span>
                </div>
                <div className="text-[10px] text-slate-500 italic">{report.body.positioning}</div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">REGISTERED WALLETS ({report.body.registered_wallets.length})</div>
                  {report.body.registered_wallets.map((w) => (
                    <div key={w.wallet_id} className="border-l border-[#2D323E] pl-3 mb-2 text-slate-300">
                      {w.name} — {w.legal_owner} · {w.custody_type.replace(/_/g, " ")} · {w.network} · {w.status} · proof-of-control {w.proof_of_control}
                      {w.addresses.map((a) => <div key={a} className="text-slate-500">addr: {a}</div>)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">HOLDINGS ({report.body.holdings.length})</div>
                  {report.body.holdings.map((h) => (
                    <div key={`${h.wallet_id}-${h.asset}`} className="text-slate-300">
                      {h.asset} ({h.network}) — {fmtQty(h.quantity)} @ {h.wallet_name} · owner {h.legal_owner} · basis {h.cost_basis == null ? "unknown" : `$${h.cost_basis.toLocaleString()}`} · provenance {h.provenance_status} · recon {h.reconciliation_status}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">OWNERSHIP CLASSIFICATIONS ({report.body.ownership_classifications.length})</div>
                  {report.body.ownership_classifications.map((e) => (
                    <div key={e.ownership_event_id} className="text-slate-300">
                      {e.ownership_event_id} — {e.asset} {fmtQty(e.quantity)} · {e.classification.replace(/_/g, " ")} · {e.legal_owner_before || "—"} → {e.legal_owner_after} · tax {e.tax_treatment_status?.replace(/_/g, " ")} · {e.approval_state}{e.superseded ? " · SUPERSEDED" : ""}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">BLOCKCHAIN TRANSACTIONS ({report.body.blockchain_transactions.length})</div>
                  {report.body.blockchain_transactions.map((t) => (
                    <div key={t.transfer_id} className="text-slate-300">
                      {t.transfer_id} — {t.asset}/{t.network} {fmtQty(t.quantity_sent)} → received {t.quantity_received == null ? "—" : fmtQty(t.quantity_received)} · tx {t.tx_hash || "—"} · {t.confirmations ?? "—"} conf · {t.reconciliation_state}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">RECONCILIATION RESULTS ({report.body.reconciliation_results.length})</div>
                  {report.body.reconciliation_results.map((r) => (
                    <div key={r.reconciliation_id} className="text-slate-300">
                      {r.reconciliation_id} — {r.asset} @ {r.wallet_name}: expected {fmtQty(r.expected_quantity)} observed {r.observed_quantity == null ? "—" : fmtQty(r.observed_quantity)} variance {r.variance == null ? "—" : fmtQty(r.variance)} · {r.provider} · {r.status} · {fmtTime(r.at)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 mb-1">EVIDENCE INVENTORY ({report.body.evidence_inventory.length})</div>
                  {report.body.evidence_inventory.map((d) => (
                    <div key={d.evidence_id} className="text-slate-300">
                      {d.evidence_id} v{d.version} — {d.document_type.replace(/_/g, " ")} · sha256 {d.file_hash.slice(0, 20)}…{d.superseded ? " · SUPERSEDED" : ""}
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500">
                  Audit references: {report.body.audit_references.join(", ") || "—"} · Suitable for: {report.body.suitability.join(", ")}.
                  This report contains public data only — no keys, secrets, or signing material.
                </div>
              </div>
            </Panel>
          )}
          {!report && reports.length === 0 && (
            <div className="panel p-8 text-center text-slate-600 font-mono text-xs" data-testid="report-empty">
              Generate the Hussar Holdings Digital Asset Custody &amp; Provenance Report for accountant review, lender/investor due diligence, internal governance, or legal documentation.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
